import React, { useState } from 'react';
import { Share2, X } from 'lucide-react';
import { getCurrentVisit } from '../../utils/visitCount';

const STORAGE_KEY = 'install_hint_shown';
// Erst ab dem dritten Besuch – wer die Seite einmal ansieht, will sie nicht installieren.
const MIN_VISITS = 3;

/** Läuft die App bereits als installierte PWA? */
const isStandalone = () =>
  window.matchMedia?.('(display-mode: standalone)').matches ||
  // iOS Safari kennt display-mode erst spät und meldet es hierüber.
  window.navigator.standalone === true;

const isIos = () => /iphone|ipad|ipod/i.test(navigator.userAgent);

/**
 * InstallHint – Hinweis, die App auf den Startbildschirm zu legen.
 *
 * Zwei Wege, weil iOS kein `beforeinstallprompt` kennt:
 *   - Chrome/Android: gespeichertes Prompt-Event, Button startet den Dialog
 *   - iOS Safari: Anleitung über das Teilen-Menü
 *
 * Wird erst nach Splash und Cookie-Hinweis gerendert (s. App.jsx).
 */
const InstallHint = ({ installPrompt }) => {
  const [hidden, setHidden] = useState(() => {
    try {
      return Boolean(localStorage.getItem(STORAGE_KEY));
    } catch {
      return true; // Kein Speicher = kein Merken = lieber gar nicht zeigen.
    }
  });

  const mode = installPrompt ? 'prompt' : isIos() ? 'ios' : null;

  if (hidden || !mode) return null;
  if (isStandalone()) return null;
  if (getCurrentVisit() < MIN_VISITS) return null;

  const remember = () => {
    try {
      localStorage.setItem(STORAGE_KEY, new Date().toISOString());
    } catch {
      // Ohne Speicher erscheint der Hinweis beim nächsten Mal erneut.
    }
    setHidden(true);
  };

  const handleInstall = async () => {
    remember();
    try {
      await installPrompt.prompt();
      await installPrompt.userChoice;
    } catch {
      // Prompt-Events sind einmalig; ein zweiter Aufruf wirft.
    }
  };

  return (
    <div className="install-hint" role="region" aria-label="App installieren">
      <div className="install-hint__content">
        {mode === 'ios' && <Share2 size={20} className="install-hint__icon" aria-hidden="true" />}
        <p className="install-hint__text body Font-S-B-130-T">
          {mode === 'prompt'
            ? 'Grosskinder Aktiv als App speichern – schneller starten, kein Browser nötig.'
            : 'Zum Startbildschirm hinzufügen: Tippe auf das Teilen-Symbol und wähle «Zum Home-Bildschirm».'}
        </p>
        {mode === 'prompt' && (
          <button type="button" className="btn btn-primary install-hint__btn" onClick={handleInstall}>
            Installieren
          </button>
        )}
        <button
          type="button"
          className="install-hint__close"
          onClick={remember}
          aria-label="Hinweis schliessen"
        >
          <X size={20} aria-hidden="true" />
        </button>
      </div>
    </div>
  );
};

export default InstallHint;
